import { zodResolver } from '@hookform/resolvers/zod'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import type { SxProps, Theme } from '@mui/material'
import { Alert, Button, IconButton, InputAdornment, Stack, Tooltip } from '@mui/material'
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import axios from 'axios'
import { DateTime } from 'luxon'
import { useState } from 'react'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import FloatTextField from '@/components/inputs/FloatTextField'
import { useAlertContext } from '@/contexts/AlertContext'
import type { ValidationErrorResponse } from '@/features/resource/endpoints/resources'
import { useCancelTargetSoCMutation, useSetTargetSoCMutation } from '@/features/resource/hooks/useTargetSoCMutation'
import { getTargetSoCSchema } from '@/features/resource/schemas'
import type { TargetSoCFormValues } from '@/features/resource/utils/targetSoC'
import {
  buildTargetSoCRequestBody,
  defaultValidUntil,
  parseValidatedTargetSoCFormValues,
} from '@/features/resource/utils/targetSoC'

type TargetSoCFormProps = Readonly<{
  resourceId: string
}>

/**
 * Extract a server-side validation message to show inline (vs. a generic toast). Treats a response
 * as a validation failure when the HTTP status is 400/422 or the body is tagged
 * `VALIDATION_FAILED`; anything else (500, network, non-Axios) is left for the generic toast.
 */
function extractValidationMessage(error: unknown): string | null {
  if (!axios.isAxiosError(error)) return null
  const status = error.response?.status
  const data = error.response?.data as Partial<ValidationErrorResponse> | undefined
  const isValidationFailure = status === 400 || status === 422 || data?.status === 'VALIDATION_FAILED'
  if (!isValidationFailure || !data?.message) return null
  return data.message
}

export function TargetSoCForm({ resourceId }: TargetSoCFormProps) {
  const { t } = useTranslation()
  const { pushAlert } = useAlertContext()
  const { setTargetSoC, isPending: isSubmitting } = useSetTargetSoCMutation()
  const { cancelTargetSoC, isPending: isCancelling } = useCancelTargetSoCMutation()
  const [serverError, setServerError] = useState<string | null>(null)

  // A command is in flight when either the set or the cancel request is pending; both buttons stay
  // disabled meanwhile so a fast Send→Cancel can't fire two conflicting commands on one resource.
  const isBusy = isSubmitting || isCancelling

  const form = useForm<TargetSoCFormValues>({
    resolver: zodResolver(getTargetSoCSchema(t)),
    defaultValues: {
      targetSoCPercent: null,
      maxSetpointChargeKw: null,
      maxSetpointDischargeKw: null,
      maxRampSpeedChargeKwPerS: null,
      maxRampSpeedDischargeKwPerS: null,
      validUntil: defaultValidUntil(),
    },
  })

  const { control, handleSubmit } = form

  const onSubmit = async (values: TargetSoCFormValues) => {
    // The schema has already guaranteed the required fields; parsing narrows the types so the
    // request builder is total. `null` here would mean a schema/desync bug — treat as a no-op.
    const validated = parseValidatedTargetSoCFormValues(values)
    if (validated === null) return

    setServerError(null)
    try {
      await setTargetSoC({ resourceId, body: buildTargetSoCRequestBody(validated) })
      pushAlert({ message: t('resources.soc_management.form.success'), severity: 'success' })
    } catch (error) {
      handleCommandError(error, 'resources.soc_management.form.error')
    }
  }

  const onCancel = async () => {
    setServerError(null)
    try {
      await cancelTargetSoC({ resourceId })
      pushAlert({ message: t('resources.soc_management.form.cancel_success'), severity: 'success' })
    } catch (error) {
      handleCommandError(error, 'resources.soc_management.form.cancel_error')
    }
  }

  // Surface a server-side validation message inline; fall back to a generic toast otherwise.
  function handleCommandError(error: unknown, genericMessageKey: string) {
    const inline = extractValidationMessage(error)
    if (inline) {
      setServerError(inline)
    } else {
      pushAlert({ message: t(genericMessageKey as never), severity: 'error' })
    }
  }

  return (
    <FormProvider {...form}>
      <form aria-label={t('resources.soc_management.form.label')} onSubmit={handleSubmit(onSubmit)}>
        <Stack direction="column" spacing={3}>
          <CustomTypography variant="h3">{t('resources.soc_management.form.title')}</CustomTypography>

          {serverError !== null && <Alert severity="error">{serverError}</Alert>}

          <NumericField
            required
            max={100}
            min={0}
            name="targetSoCPercent"
            step={1}
            tooltipAriaKey="resources.soc_management.form.target_soc_info_aria"
            tooltipKey="resources.soc_management.form.target_soc_tooltip"
            unit="%"
          />
          <NumericField min={0} name="maxSetpointChargeKw" numberOfDecimals={3} step={50} unit="kW" />
          <NumericField min={0} name="maxSetpointDischargeKw" numberOfDecimals={3} step={50} unit="kW" />
          <NumericField min={0} name="maxRampSpeedChargeKwPerS" numberOfDecimals={3} step={0.1} unit="kW/s" />
          <NumericField min={0} name="maxRampSpeedDischargeKwPerS" numberOfDecimals={3} step={0.1} unit="kW/s" />

          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <Controller
              control={control}
              name="validUntil"
              render={({ field, fieldState: { error } }) => (
                // `validUntil` is widely misread as a steering deadline; the info tooltip spells out
                // that it only gates delivery/acceptance, not how long the mission runs.
                <Stack alignItems="flex-start" direction="row" spacing={1}>
                  <DateTimePicker
                    ampm={false}
                    enableAccessibleFieldDOMStructure={false}
                    label={t('resources.soc_management.form.fields.validUntil')}
                    minDateTime={DateTime.now()}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: Boolean(error),
                        helperText: error?.message,
                      },
                    }}
                    value={field.value ? DateTime.fromJSDate(field.value) : null}
                    onChange={(newValue) => field.onChange(newValue ? newValue.toJSDate() : null)}
                  />
                  <InfoTooltip
                    ariaLabel={t('resources.soc_management.form.valid_until_info_aria')}
                    sx={{ mt: 1 }}
                    title={t('resources.soc_management.form.valid_until_tooltip')}
                  />
                </Stack>
              )}
            />
          </LocalizationProvider>

          <Stack direction="row" spacing={2}>
            {/* Tooltips wrap a span so they still surface on hover/focus when the button is disabled
             * (MUI suppresses events on a disabled button itself). Operators can't see active
             * commands in the UI, so the copy spells out what each action does. */}
            <Tooltip title={t('resources.soc_management.form.submit_tooltip')}>
              <span>
                <Button disabled={isBusy} type="submit" variant="contained">
                  {t('resources.soc_management.form.submit')}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={t('resources.soc_management.form.cancel_tooltip')}>
              <span>
                <Button disabled={isBusy} type="button" variant="outlined" onClick={onCancel}>
                  {t('resources.soc_management.form.cancel')}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </form>
    </FormProvider>
  )
}

/** Keyboard-focusable info-icon that reveals help text on hover/focus. */
function InfoTooltip({
  title,
  ariaLabel,
  edge,
  sx,
}: Readonly<{ title: string; ariaLabel: string; edge?: 'start' | 'end' | false; sx?: SxProps<Theme> }>) {
  return (
    <Tooltip title={title}>
      <IconButton aria-label={ariaLabel} edge={edge} size="small" sx={sx}>
        <HelpOutlineIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  )
}

type NumericFieldName = {
  [K in keyof TargetSoCFormValues]: TargetSoCFormValues[K] extends number | null ? K : never
}[keyof TargetSoCFormValues]

type NumericFieldProps = Readonly<{
  name: NumericFieldName
  unit: string
  step: number
  min?: number
  max?: number
  numberOfDecimals?: number
  required?: boolean
  /** Optional i18n key for an info-icon tooltip rendered as the input's end-adornment. */
  tooltipKey?: string
  /** Optional i18n key for a concise aria-label on the info-icon button (the long tooltip text
   * would otherwise be announced as the button name). Falls back to the tooltip text. */
  tooltipAriaKey?: string
}>

function NumericField({
  name,
  unit,
  step,
  min,
  max,
  numberOfDecimals = 2,
  required,
  tooltipKey,
  tooltipAriaKey,
}: NumericFieldProps) {
  const { t } = useTranslation()
  const {
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<TargetSoCFormValues>()

  const initial = getValues(name)
  const error = errors[name]?.message as string | undefined
  const label = t(`resources.soc_management.form.fields.${name}` as never)
  const tooltipText = tooltipKey ? t(tooltipKey as never) : null
  const tooltipAria = tooltipAriaKey ? t(tooltipAriaKey as never) : tooltipText

  return (
    <FloatTextField
      // Pass undefined for an empty optional field — there's no numeric value to seed.
      error={Boolean(error)}
      initialValue={typeof initial === 'number' ? String(initial) : undefined}
      max={max}
      min={min}
      numberOfDecimals={numberOfDecimals}
      step={step}
      textFieldProps={{
        label,
        required,
        fullWidth: true,
        helperText: error,
        InputProps: {
          endAdornment: (
            <InputAdornment position="end">
              {unit}
              {tooltipText !== null && tooltipAria !== null && (
                <InfoTooltip ariaLabel={tooltipAria} edge="end" title={tooltipText} />
              )}
            </InputAdornment>
          ),
        },
      }}
      onValueChange={(value) => setValue(name, value ?? null, { shouldValidate: true })}
    />
  )
}
